import React, { useState } from 'react';
import { Plus, UserPlus, Users as UsersIcon, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../layout';
import { UserTable } from '../users/user-table';
import { UserFormModal } from '../users/user-form-modal';
import { UserImportModal } from '../users/user-import-modal';
import { useDivisionUsers, useDeleteDivisionUser } from '../../hooks/use-division-users';
import { useDivisions } from '../../hooks/use-divisions';

interface DivisionUsersManagementProps {
  selectedDivisionId: string;
  setSelectedDivisionId: (id: string) => void;
}

export function DivisionUsersManagement({
  selectedDivisionId,
  setSelectedDivisionId,
}: DivisionUsersManagementProps) {
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);

  // Fetch divisions for the dropdown
  const { data: divisionsResponse } = useDivisions({ active: true });
  const divisions = divisionsResponse?.divisions || [];

  // Fetch users for selected division
  const {
    data: usersData,
    isLoading: isLoadingUsers,
  } = useDivisionUsers(
    selectedDivisionId,
    {
      active: activeFilter,
      search: searchQuery || undefined,
      limit: 50,
      offset: 0,
    },
    !!selectedDivisionId
  );

  const deleteUserMutation = useDeleteDivisionUser(selectedDivisionId);

  const handleCreateUser = () => {
    setEditingUserId(undefined);
    setIsUserFormOpen(true);
  };

  const handleEditUser = (userId: string) => {
    setEditingUserId(userId);
    setIsUserFormOpen(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this user from the division?')) {
      return;
    }

    try {
      await deleteUserMutation.mutateAsync(userId);
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleCloseUserForm = () => {
    setIsUserFormOpen(false);
    setEditingUserId(undefined);
  };

  const handleImportComplete = () => {
    // Refetch will happen automatically via the hook
    setIsImportModalOpen(false);
  };

  const users = usersData?.users || [];
  const totalUsers = usersData?.totalCount || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-md font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
            <UsersIcon className="h-5 w-5" />
            Division Users
          </h4>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            Manage user access and roles within divisions
          </p>
        </div>
      </div>

      {/* Division Selector and Actions */}
      <Card>
        <div className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Division Selector */}
            <div className="flex items-center gap-4 flex-1">
              <label className="text-sm font-medium text-secondary-700 dark:text-secondary-200 whitespace-nowrap">
                Division:
              </label>
              <select
                value={selectedDivisionId}
                onChange={(e) => setSelectedDivisionId(e.target.value)}
                className="flex-1 max-w-xs rounded-md border border-secondary-300 dark:border-secondary-600 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              >
                <option value="">Select a division...</option>
                {divisions.map((division) => (
                  <option key={division.id} value={division.id}>
                    {division.name} {division.code ? `(${division.code})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Search and Filter */}
            {selectedDivisionId && (
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-md text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                />
                <select
                  value={activeFilter === undefined ? 'all' : activeFilter ? 'active' : 'inactive'}
                  onChange={(e) => {
                    const value = e.target.value;
                    setActiveFilter(
                      value === 'all' ? undefined : value === 'active'
                    );
                  }}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-md text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                >
                  <option value="all">All Users</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <Button onClick={() => setIsImportModalOpen(true)} variant="outline" size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
                <Button onClick={handleCreateUser} size="sm">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Users Table */}
      {selectedDivisionId ? (
        <Card>
          <div className="p-6">
            {isLoadingUsers ? (
              <div className="text-center py-8 text-secondary-600 dark:text-secondary-400">
                Loading users...
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <UsersIcon className="mx-auto h-12 w-12 text-secondary-400" />
                <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">No users found</h3>
                <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                  {searchQuery
                    ? 'No users match your search criteria.'
                    : 'Get started by adding users to this division.'}
                </p>
                {!searchQuery && (
                  <div className="mt-6">
                    <Button onClick={handleCreateUser}>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add First User
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    Showing {users.length} of {totalUsers} users
                  </p>
                </div>
                <UserTable
                  users={users}
                  totalCount={totalUsers}
                  currentPage={1}
                  pageSize={50}
                  totalPages={1}
                  isLoading={false}
                  error={null}
                  divisionId={selectedDivisionId}
                  onPageChange={() => {}}
                  onUserClick={handleEditUser}
                  onDeleteUser={handleDeleteUser}
                  isDeleting={deleteUserMutation.isPending}
                />
              </div>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <div className="p-12 text-center">
            <UsersIcon className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
              Select a Division
            </h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              Choose a division from the dropdown above to manage its users.
            </p>
          </div>
        </Card>
      )}

      {/* User Form Modal */}
      {selectedDivisionId && (
        <>
          <UserFormModal
            isOpen={isUserFormOpen}
            onClose={handleCloseUserForm}
            divisionId={selectedDivisionId}
            mode={editingUserId ? 'edit' : 'create'}
            userId={editingUserId}
          />
          
          <UserImportModal
            isOpen={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            divisionId={selectedDivisionId}
            onImportComplete={handleImportComplete}
          />
        </>
      )}
    </div>
  );
}
